/**
 * Content Block Schema for Flow2
 *
 * This file defines the semantic content layer that AI generates.
 * Content blocks are pure content without layout or styling information.
 *
 * @packageDocumentation
 * @module ai/schemas
 */

/**
 * Types of content blocks that AI can generate
 */
export type ContentBlockType =
  | 'title'
  | 'kicker'
  | 'subtitle'
  | 'body'
  | 'bullet-list'
  | 'numbered-list'
  | 'quote'
  | 'callout'
  | 'image-placeholder'
  | 'video-placeholder'
  | 'stat'
  | 'chart'
  | 'chart-data'
  | 'table-data'
  | 'speaker-notes'
  | 'code-block'
  | 'caption'
  | 'toc-list'
  | 'timeline';

/**
 * Base interface for all content blocks
 */
interface ContentBlockBase {
  /** Unique identifier for this block */
  id: string;
  /** Type of content block */
  type: ContentBlockType;
  /** Optional semantic role for accessibility */
  role?: 'heading' | 'paragraph' | 'list' | 'figure' | 'complementary';
  /** Optional metadata for AI context */
  metadata?: Record<string, unknown>;
}

/**
 * Title content block - main heading text
 */
export interface TitleBlock extends ContentBlockBase {
  type: 'title';
  text: string;
  level?: 1 | 2 | 3;
}

/**
 * Kicker content block - short uppercase eyebrow label rendered as a pill
 * above the title. Optional leading icon from the closed allowlist
 * (see icon_allowlist_helper.php / FlowIcons registry).
 */
export interface KickerBlock extends ContentBlockBase {
  type: 'kicker';
  text: string;
  /** Canonical icon name from the allowlist; unknown names are dropped on save. */
  icon?: string;
}

/**
 * Subtitle content block - secondary heading
 */
export interface SubtitleBlock extends ContentBlockBase {
  type: 'subtitle';
  text: string;
}

/**
 * Body text content block - paragraph text
 */
export interface BodyBlock extends ContentBlockBase {
  type: 'body';
  text: string;
  emphasis?: 'normal' | 'strong' | 'subtle';
}

/**
 * Bullet list content block
 */
export interface BulletListBlock extends ContentBlockBase {
  type: 'bullet-list';
  items: string[];
  /** Optional nested structure */
  nestedItems?: { parent: number; items: string[] }[];
}

/**
 * Numbered list content block
 */
export interface NumberedListBlock extends ContentBlockBase {
  type: 'numbered-list';
  items: string[];
  startNumber?: number;
}

/**
 * Quote content block - for testimonials or citations
 */
export interface QuoteBlock extends ContentBlockBase {
  type: 'quote';
  text: string;
  attribution?: string;
  source?: string;
}

/**
 * Callout content block - for emphasis or key points
 */
export interface CalloutBlock extends ContentBlockBase {
  type: 'callout';
  text: string;
  calloutType?: 'info' | 'warning' | 'success' | 'tip';
  icon?: string;
}

/**
 * Image placeholder content block
 */
export interface ImagePlaceholderBlock extends ContentBlockBase {
  type: 'image-placeholder';
  description: string;
  altText?: string;
  suggestedKeywords?: string[];
  aspectRatio?: '16:9' | '4:3' | '1:1' | '3:4' | '9:16';
  /** AI image generation style routing */
  imageStyle?: 'photo' | 'illustration' | 'infographic';

  /** Image sourcing strategy: 'generate' for AI, 'search' for real images */
  imageSource?: 'generate' | 'search';
  /** Search query for real-world images (when imageSource is 'search') */
  searchQuery?: string;
  /** Type of search - determines which API to use */
  searchType?: 'book' | 'person' | 'logo' | 'place' | 'product' | 'general';

  /** URL of the generated/resolved image */
  resolvedImageUrl?: string;
  /** Source of the resolved image */
  resolvedSource?: 'ai-generated' | 'google-books' | 'open-library' | 'unsplash' | 'pexels' | 'google-favicon' | 'wikimedia';
  /** Source-side ID for the resolved image (e.g. Unsplash photo id) */
  resolvedSourceId?: string;
  /** Photographer / creator name for attribution. REQUIRED display when source is 'unsplash' (per Unsplash API guidelines). */
  resolvedPhotographerName?: string;
  /** Photographer / creator profile URL with utm tracking. Pair with resolvedPhotographerName. */
  resolvedPhotographerUrl?: string;
  /** Plain-text "Photo by X on Unsplash" string, ready to render. */
  resolvedAttribution?: string;
  /** HTML version of the attribution with anchor tags + utm params. */
  resolvedAttributionHtml?: string;
  /** Current image generation status */
  imageStatus?: 'pending' | 'generating' | 'searching' | 'complete' | 'error';
  /** Error message if generation failed */
  imageError?: string;
}

/**
 * Video placeholder content block
 */
export interface VideoPlaceholderBlock extends ContentBlockBase {
  type: 'video-placeholder';
  description: string;
  suggestedDuration?: number;
  suggestedKeywords?: string[];
}

/**
 * Stat block - big numbers for KPIs, metrics, statistics
 */
export interface StatBlock extends ContentBlockBase {
  type: 'stat';
  /** The main value (e.g., "85%", "$2.5M", "3x", "500K+") */
  value: string;
  /** Label describing the stat (e.g., "Revenue Growth", "Active Users") */
  label: string;
  /** Optional context (e.g., "vs last year", "and growing") */
  context?: string;
  /** Trend indicator */
  trend?: 'up' | 'down' | 'neutral';
}

/**
 * Chart block - data visualizations rendered as SVG
 */
export interface ChartBlock extends ContentBlockBase {
  type: 'chart';
  chartType: 'bar' | 'pie' | 'donut';
  title?: string;
  data: { label: string; value: number; color?: string }[];
}

/**
 * Chart data content block (legacy - kept for compatibility)
 */
export interface ChartDataBlock extends ContentBlockBase {
  type: 'chart-data';
  chartType: 'bar' | 'line' | 'pie' | 'donut' | 'area';
  title?: string;
  data: {
    labels: string[];
    values: number[];
    series?: { name: string; values: number[] }[];
  };
}

/**
 * Table data content block
 */
export interface TableDataBlock extends ContentBlockBase {
  type: 'table-data';
  title?: string;
  headers: string[];
  rows: string[][];
}

/**
 * Speaker notes content block
 */
export interface SpeakerNotesBlock extends ContentBlockBase {
  type: 'speaker-notes';
  text: string;
  cues?: string[];
}

/**
 * Code block content
 */
export interface CodeBlockContent extends ContentBlockBase {
  type: 'code-block';
  code: string;
  language?: string;
  filename?: string;
}

/**
 * Caption content block
 */
export interface CaptionBlock extends ContentBlockBase {
  type: 'caption';
  text: string;
  forBlockId?: string;
}

/**
 * Table of Contents entry - represents one screen in the TOC
 */
export interface TOCEntry {
  /** The screen index to navigate to */
  screenIndex: number;
  /** Title of the screen */
  title: string;
  /** Icon for the screen (number string for content, empty for sections) */
  icon: string;
  /** Optional thumbnail image URL */
  imageUrl?: string;
  /** Screen ID for navigation (populated during compilation) */
  targetScreenId?: string;
  /** True if this is a section header (displayed as group label, not numbered) */
  isSection?: boolean;
  /** Content number within the presentation (e.g., "1", "2", "3") */
  contentNumber?: number;
}

/**
 * Table of Contents list block - auto-generated navigation
 */
export interface TOCListBlock extends ContentBlockBase {
  type: 'toc-list';
  entries: TOCEntry[];
}

/**
 * Timeline entry - represents one event on the timeline
 */
export interface TimelineEntry {
  /** Label for the timeline point (e.g., "2020", "Q1", "Phase 1") */
  label: string;
  /** Title of the event */
  title: string;
  /** Optional description text */
  description?: string;
  /** Optional icon/emoji */
  icon?: string;
}

/**
 * Timeline block - chronological events display
 * Max 7 events per screen; overflow creates additional screens
 */
export interface TimelineBlock extends ContentBlockBase {
  type: 'timeline';
  entries: TimelineEntry[];
  /** Orientation hint (compiler decides based on viewport) */
  orientation?: 'horizontal' | 'vertical' | 'auto';
}

/**
 * Reveal Diagram - a single conceptual relationship, drawn for progressive
 * reveal on a phone (see docs/reveal-diagram-spec.md). Content-layer-only:
 * no MMO twin. The hero block of its screen, paired with a kicker + headline.
 */
export interface DiagramNode {
  /** Short label, <=4 words (hard cap) */
  label: string;
  /** Optional icon name from the shared allowlist (include/js/icons/registry.js) */
  icon?: string;
  /** Optional one-liner (<=12 words) shown only while this node is the active/emphasized one */
  detail?: string;
}

/**
 * Diagram block - a choreographed-reveal relationship diagram.
 * Per-template node caps are enforced at author + generation time.
 *   loop: 2-4 (5 max), transform: 2 (3 max), path: <=5, split: 1+2-3, stack: <=4
 */
export interface DiagramBlock extends ContentBlockBase {
  type: 'diagram';
  /** Relationship shape; drives layout + which validation applies */
  template: 'loop' | 'transform' | 'path' | 'split' | 'stack';
  /** 'step' advances one node+connector per beat; 'all' renders complete. Default 'step'. */
  reveal?: 'step' | 'all';
  nodes: DiagramNode[];
  /** One line under the diagram - the "so what" */
  caption?: string;
}

/**
 * Union type for all content blocks
 */
export type ContentBlock =
  | TitleBlock
  | KickerBlock
  | SubtitleBlock
  | BodyBlock
  | BulletListBlock
  | NumberedListBlock
  | QuoteBlock
  | CalloutBlock
  | ImagePlaceholderBlock
  | VideoPlaceholderBlock
  | StatBlock
  | ChartBlock
  | ChartDataBlock
  | TableDataBlock
  | SpeakerNotesBlock
  | CodeBlockContent
  | CaptionBlock
  | TOCListBlock
  | TimelineBlock
  | DiagramBlock;

/**
 * Screen types that define the purpose/layout hint of a screen
 */
export type ScreenType =
  | 'title-slide'
  | 'section-header'
  | 'content'
  | 'two-column'
  | 'image-focus'
  | 'quote-slide'
  | 'comparison'
  | 'timeline'
  | 'team'
  | 'closing'
  | 'blank'
  | 'toc'
  | 'big-stat'
  | 'stats-grid'
  | 'chart-focus';

/**
 * Screen content - represents all content on a single screen
 */
export interface ScreenContent {
  /** Unique identifier for this screen */
  id: string;
  /** Type of screen layout hint */
  screenType: ScreenType;
  /** Content blocks on this screen */
  blocks: ContentBlock[];
  /** Background hint for styling */
  backgroundHint?: 'light' | 'dark' | 'image' | 'gradient';
  /** Transition hint */
  transitionHint?: 'slide' | 'fade' | 'instant';
}

/**
 * Presentation content - the complete content model for a presentation
 */
export interface PresentationContent {
  /** Unique identifier for this presentation */
  id: string;
  /** Presentation title */
  title: string;
  /** All screens in the presentation */
  screens: ScreenContent[];
  /** Global metadata about the presentation */
  globalMetadata: PresentationMetadata;
  /** Version for schema compatibility */
  schemaVersion: string;
}

/**
 * Metadata about the presentation for AI context
 */
export interface PresentationMetadata {
  /** Main topic or subject */
  topic: string;
  /** Tone of the presentation */
  tone: 'professional' | 'casual' | 'formal' | 'creative' | 'educational';
  /** Target audience */
  audience?: string;
  /** Purpose of the presentation */
  purpose?: 'inform' | 'persuade' | 'educate' | 'entertain' | 'pitch';
  /** Industry or domain */
  industry?: string;
  /** Keywords for theming */
  keywords?: string[];
  /** Estimated duration in minutes */
  estimatedDuration?: number;
  /** Language */
  language?: string;
  /** AI-suggested theme ID based on content analysis */
  suggestedThemeId?: string;
}

/**
 * Utility function to create a unique ID for blocks
 */
export function generateBlockId(): string {
  const S4 = () => (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  return `block-${S4()}${S4()}`;
}

/**
 * Utility function to create a unique ID for screens
 */
export function generateScreenId(): string {
  const S4 = () => (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  return `screen-${S4()}${S4()}`;
}

/**
 * Utility function to create a unique ID for presentations
 */
export function generatePresentationId(): string {
  const S4 = () => (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  return `pres-${S4()}${S4()}-${S4()}-${S4()}`;
}

/**
 * Type guard to check if a block is a text-based block
 */
export function isTextBlock(block: ContentBlock): block is TitleBlock | SubtitleBlock | BodyBlock | QuoteBlock | CalloutBlock | CaptionBlock {
  return ['title', 'subtitle', 'body', 'quote', 'callout', 'caption'].includes(block.type);
}

/**
 * Type guard to check if a block is a list block
 */
export function isListBlock(block: ContentBlock): block is BulletListBlock | NumberedListBlock {
  return ['bullet-list', 'numbered-list'].includes(block.type);
}

/**
 * Type guard to check if a block is a media placeholder
 */
export function isMediaPlaceholder(block: ContentBlock): block is ImagePlaceholderBlock | VideoPlaceholderBlock {
  return ['image-placeholder', 'video-placeholder'].includes(block.type);
}

/**
 * Type guard to check if a block is a data block
 */
export function isDataBlock(block: ContentBlock): block is StatBlock | ChartBlock | ChartDataBlock | TableDataBlock {
  return ['stat', 'chart', 'chart-data', 'table-data'].includes(block.type);
}

/**
 * Factory function to create a title block
 */
export function createTitleBlock(text: string, level: 1 | 2 | 3 = 1): TitleBlock {
  return {
    id: generateBlockId(),
    type: 'title',
    text,
    level,
    role: 'heading',
  };
}

/**
 * Factory function to create a subtitle block
 */
export function createSubtitleBlock(text: string): SubtitleBlock {
  return {
    id: generateBlockId(),
    type: 'subtitle',
    text,
    role: 'heading',
  };
}

/**
 * Factory function to create a body block
 */
export function createBodyBlock(text: string, emphasis: 'normal' | 'strong' | 'subtle' = 'normal'): BodyBlock {
  return {
    id: generateBlockId(),
    type: 'body',
    text,
    emphasis,
    role: 'paragraph',
  };
}

/**
 * Factory function to create a bullet list block
 */
export function createBulletListBlock(items: string[]): BulletListBlock {
  return {
    id: generateBlockId(),
    type: 'bullet-list',
    items,
    role: 'list',
  };
}

/**
 * Factory function to create a numbered list block
 */
export function createNumberedListBlock(items: string[], startNumber: number = 1): NumberedListBlock {
  return {
    id: generateBlockId(),
    type: 'numbered-list',
    items,
    startNumber,
    role: 'list',
  };
}

/**
 * Factory function to create a quote block
 */
export function createQuoteBlock(text: string, attribution?: string, source?: string): QuoteBlock {
  return {
    id: generateBlockId(),
    type: 'quote',
    text,
    attribution,
    source,
  };
}

/**
 * Factory function to create a callout block
 */
export function createCalloutBlock(
  text: string,
  calloutType: 'info' | 'warning' | 'success' | 'tip' = 'info'
): CalloutBlock {
  return {
    id: generateBlockId(),
    type: 'callout',
    text,
    calloutType,
    role: 'complementary',
  };
}

/**
 * Factory function to create an image placeholder block
 */
export function createImagePlaceholderBlock(
  description: string,
  aspectRatio: '16:9' | '4:3' | '1:1' | '3:4' | '9:16' = '16:9'
): ImagePlaceholderBlock {
  return {
    id: generateBlockId(),
    type: 'image-placeholder',
    description,
    aspectRatio,
    role: 'figure',
  };
}

/**
 * Factory function to create a speaker notes block
 */
export function createSpeakerNotesBlock(text: string, cues?: string[]): SpeakerNotesBlock {
  return {
    id: generateBlockId(),
    type: 'speaker-notes',
    text,
    cues,
  };
}

/**
 * Factory function to create a screen content object
 */
export function createScreenContent(
  screenType: ScreenType,
  blocks: ContentBlock[],
  backgroundHint?: 'light' | 'dark' | 'image' | 'gradient'
): ScreenContent {
  return {
    id: generateScreenId(),
    screenType,
    blocks,
    backgroundHint,
  };
}

/**
 * Factory function to create a presentation content object
 */
export function createPresentationContent(
  title: string,
  screens: ScreenContent[],
  metadata: Partial<PresentationMetadata>
): PresentationContent {
  return {
    id: generatePresentationId(),
    title,
    screens,
    globalMetadata: {
      topic: metadata.topic || title,
      tone: metadata.tone || 'professional',
      audience: metadata.audience,
      purpose: metadata.purpose,
      industry: metadata.industry,
      keywords: metadata.keywords,
      estimatedDuration: metadata.estimatedDuration,
      language: metadata.language || 'en',
    },
    schemaVersion: '1.0.0',
  };
}

/**
 * Current schema version
 */
export const CONTENT_SCHEMA_VERSION = '1.0.0';
